'use client';

import type { FC, ReactNode } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { formatDayMonthTime } from '../../i18n/formatDate';
import { pluralize } from '../../i18n/pluralize';
import { BlurImage } from '../ui/BlurImage';
import translations from './RainHistoryList.translations';
import { RiArrowRightSLine } from '../icons';
import { RainWinnerRow } from './RainWinnerRow';
import type { RainHistoryEntry } from './types';

interface RainHistoryListProps {
  rounds: RainHistoryEntry[];
  onOpenUser?: (userId: string) => void;
  onOpenRound?: (rainId: string) => void;
  className?: string;
}

export const RainHistoryList: FC<RainHistoryListProps> = ({ rounds, onOpenUser, onOpenRound, className = '' }) => {
  const { t, lang } = useLocalTranslation(translations);

  const winnersCount = (n: number) =>
    pluralize(
      n,
      { one: t('winnersOne', { count: n }), few: t('winnersFew', { count: n }), many: t('winnersMany', { count: n }) },
      lang,
    );

  if (rounds.length === 0) {
    return (
      <div className={`p-4 ${className}`}>
        <div className="rounded-xl bg-fill-tertiary px-3 py-6 text-center text-sm text-label-tertiary">
          {t('empty')}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col gap-3 p-4 ${className}`}>
      {rounds.map((round) => (
        <div key={round.id} className="overflow-hidden rounded-2xl bg-fill-tertiary">
          <RoundHeader round={round} onClick={onOpenRound ? () => onOpenRound(round.id) : undefined}>
            <BlurImage
              src={round.case.imageUrl}
              placeholder={round.case.imagePlaceholder}
              alt={round.case.name}
              className="size-10 shrink-0 rounded-lg"
            />
            <div className="min-w-0 flex-1 leading-tight">
              <div className="truncate text-base font-semibold text-label-primary">{round.case.name}</div>
              <div className="truncate text-xs text-label-tertiary">
                {formatDayMonthTime(round.finalizedAt, lang)} · {winnersCount(round.winners.length)}
              </div>
            </div>
            {onOpenRound && <RiArrowRightSLine className="shrink-0 text-xl text-label-tertiary rtl:-scale-x-100" />}
          </RoundHeader>

          <div className="flex flex-col">
            {round.winners.map((winner) => (
              <RainWinnerRow
                key={`${round.id}-${winner.userId}`}
                winner={winner}
                onClick={onOpenUser ? () => onOpenUser(winner.userId) : undefined}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

const RoundHeader: FC<{ round: RainHistoryEntry; onClick?: () => void; children: ReactNode }> = ({
  onClick,
  children,
}) => {
  const box = 'flex w-full items-center gap-3 px-3.5 py-3 text-start';
  if (!onClick) return <div className={box}>{children}</div>;
  return (
    <button type="button" onClick={onClick} className={`${box} transition active:opacity-80`}>
      {children}
    </button>
  );
};
